import type { ReactNode } from 'react';
import {
  FunnelChart,
  Funnel,
  LabelList,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { StageStat } from '../../../api';

interface PipelineFunnelProps {
  stages: StageStat[];
}

const COLORS = [
  '#2563eb', // blue-600
  '#4f46e5', // indigo-600
  '#7c3aed', // violet-600
  '#9333ea', // purple-600
  '#c026d3', // fuchsia-600
  '#db2777', // pink-600
  '#e11d48', // rose-600
];

export function PipelineFunnel({ stages }: PipelineFunnelProps): ReactNode {
  const funnelData = stages
    .filter((stage) => stage.count > 0 || stages.indexOf(stage) < 3)
    .map((stage, index) => ({
      name: stage.name,
      value: stage.count,
      amount: stage.value,
      fill: COLORS[index % COLORS.length],
    }));

  if (funnelData.length === 0) {
    return (
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Pipeline Funnel</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No pipeline data available
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Pipeline Funnel</h3>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <FunnelChart>
            <Tooltip
              content={({ payload }) => {
                if (!payload?.[0]) return null;
                const data = payload[0].payload;
                return (
                  <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                    <p className="font-semibold text-gray-900">{data.name}</p>
                    <p className="text-sm text-gray-600">
                      {data.value} deals
                    </p>
                    <p className="text-sm text-gray-600">
                      ${data.amount?.toLocaleString() || 0}
                    </p>
                  </div>
                );
              }}
            />
            <Funnel
              data={funnelData}
              dataKey="value"
              nameKey="name"
              isAnimationActive
            >
              <LabelList
                position="right"
                fill="#374151"
                stroke="none"
                dataKey="name"
                className="text-sm"
              />
              <LabelList
                position="center"
                fill="#ffffff"
                stroke="none"
                dataKey="value"
                className="font-bold"
              />
              {funnelData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Funnel>
          </FunnelChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
